import React, { useState } from 'react'
import { X } from 'lucide-react'
import MemberSearch from '@/components/matching/MemberSearch'
import { groupService } from '@/services/groupService'
import { useToast } from '@/contexts/ToastContext'
import { GroupRoom } from '@/types/group'
import { User } from '@/types/user'

interface NewMessageModalProps {
  isOpen: boolean
  onClose: () => void
  onConversationStarted: (group: GroupRoom) => void
}

const NewMessageModal: React.FC<NewMessageModalProps> = ({
  isOpen,
  onClose,
  onConversationStarted
}) => {
  const { showToast } = useToast()
  const [isStarting, setIsStarting] = useState(false)

  if (!isOpen) {
    return null
  }

  const handleSelectUser = async (user: User) => {
    if (isStarting) {
      return
    }

    try {
      setIsStarting(true)
      const conversation = await groupService.createDirect(user.id)
      onConversationStarted(conversation)
      onClose()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to start conversation'
      })
    } finally {
      setIsStarting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">New Message</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <MemberSearch onSelect={handleSelectUser} placeholder="Search for anyone by name..." />
          </div>
          {isStarting && <p className="text-sm text-gray-500">Starting conversation…</p>}
        </div>
      </div>
    </div>
  )
}

export default NewMessageModal
